import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { ModalService } from 'src/app/core/services/modal.service'
import { ConfigService } from 'src/app/core/services/config.service'
import { Subject } from 'rxjs'
import { LanguageService } from 'src/app/core/services/language.service'
import { MatIconModule } from '@angular/material/icon'
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatInputModule } from '@angular/material/input'
import { MatFormFieldModule } from '@angular/material/form-field'
import { get } from 'lodash-es'
import { II18nEntryDialogOptions } from 'src/app/core/interfaces/modal.interface'
import { TextPreviewInputComponent } from '../../controls/text-preview/text-preview.component'
import { QuillEditorComponent } from 'ngx-quill'
import { MatButtonModule } from '@angular/material/button'

@Component({
  selector: 'admin-i18n-entry-dialog-component',
  template: `
    <div mat-dialog-title class="dialog__title">
      <span>{{data.label}}</span>
      <div class="close-button-wrapper">
        <button
          mat-icon-button
          (click)="closeDialog('cancel')"
          aria-label="Close dialog"
        ><mat-icon>close</mat-icon></button>
      </div>
    </div>
    <mat-dialog-content class="dialog__content">
      <form [formGroup]="form" #htmlForm>
        @for (lang of langService.getAvailableLanguagesForAllDomains(); track lang) {
          @switch (data.type) {
            @case ('input') {
              <mat-form-field>
                <mat-label>{{lang.adminName}}</mat-label>
                <input
                  matInput
                  #dataInput
                  type="text"
                  [formControlName]="lang.code"
                  [placeholder]="getValue(data, 'placeholder')"
                  [maxlength]="data.maxLength ?? null"
                />
              </mat-form-field>
            }

            @case ('wysiwyg') {
              <quill-editor
                [modules]="quillModules"
                [formControlName]="lang.code"
                [bounds]="htmlForm"
                [styles]="{'height': '150px', 'margin-bottom': '20px'}"
                [placeholder]="getValue(data, 'placeholder')"
              >
                <div above-quill-editor-toolbar>
                  <span>{{lang.adminName}}</span>
                </div>
              </quill-editor>
            }
          }
          @if (data.preview) {
            <app-text-preview
              [text]="getValue(form.value, lang.code)"
              [format]="data.preview"
            ></app-text-preview>
          }
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions class="dialog__actions">
      <button [matButton]="'elevated'" (click)="closeDialog('apply')">Apply</button>
    </mat-dialog-actions>
  `,
  styleUrls: ['../../../../assets/admin.scss'],
  styles: [`
    @use "sass:map";
    @import 'src/assets/_colors';
    @import 'src/assets/typography';
    :host {
      display: block;
      width: 500px;
    }

    quill-editor {
      width: 100%;
    }

    .dialog {
      &__title {
        @include typography(h5);
        color: map-get($light, text, primary);
        display: flex;
        align-items: center;
        &::before {
          display: none;
        }
      }

      &__content {
        padding-top: 0 !important;
        padding-bottom: 0;
      }

      &__actions {
        padding-top: 0;
      }
    }
  `],
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    QuillEditorComponent,
    TextPreviewInputComponent,
    MatButtonModule,
  ],
})
export class AdminI18nEntryDialogComponent implements OnInit {
  data = inject<II18nEntryDialogOptions>(MAT_DIALOG_DATA)
  dialogRef = inject<MatDialogRef<AdminI18nEntryDialogComponent>>(MatDialogRef)
  protected configService = inject(ConfigService)
  protected langService = inject(LanguageService)
  private fb = inject(FormBuilder)
  private _modalService = inject(ModalService)

  window = inject(Window)
  protected submitEventNotifier = new Subject<void>()
  form: FormGroup

  protected quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link']
    ]
  }

  constructor() {
    this.form = this.fb.group({})
  }

  ngOnInit(): void {
    this.initializeFormControls()
  }

  initializeFormControls(): void {
    this.langService.getAvailableLanguagesForAllDomains().forEach(lang => {
      const langKey = this.data.key + '.' + lang.code
      this.form.addControl(lang.code, new FormControl(get(this.data.config, langKey)))
    })
  }

  getValue(data: object, key: string): string {
    return get(data, key)
  }

  closeDialog(action: 'cancel' | 'apply'): void {
    if (action !=='apply' && this.form.dirty) {
      this._modalService.confirmAdmin({
        title: 'Warning: Changes will not be saved without selecting “Apply”.',
        cancelLabel: 'Cancel',
        confirmLabel: 'Confirm',
      }).afterClosed().subscribe(choice => {
        if (choice === 'confirm') {
          this.dialogRef.close()
        }
      })
      return
    }
    this.dialogRef.close(this.form.value)
  }

}
